import React, { useEffect, useState, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import Modal from "../Modal";
import {
  FaSearch, FaPlus, FaPencilAlt, FaBan, FaCheck, FaMoneyBillWave, FaChevronLeft, FaChevronRight,
} from "react-icons/fa";

const API = "/api/salidas";
const LIMIT = 8;

const tiposNombres = {
  1: "Salidas Tesorería General",
  2: "Salidas Tesorería Misiones",
};

const formatoFecha = (fecha) =>
  fecha ? new Date(fecha).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "—";

const formatoMoneda = (valor) =>
  `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

export default function SalidasTable({ tipo, encabezadoExtra = null }) {
  const { token } = useContext(AuthContext);

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [estatusFiltro, setEstatusFiltro] = useState("activos");
  const [categorias, setCategorias] = useState([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [editando, setEditando] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const mostrarToast = (mensaje, tipoToast = "success") => {
    setToast({ mostrar: true, mensaje, tipo: tipoToast });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}?tipo=${tipo}&search=${encodeURIComponent(search)}&estatus=${estatusFiltro}&categoria=${categoriaFiltro}&page=${page}&limit=${LIMIT}`,
        { credentials: "include", headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error(error);
      mostrarToast("Error cargando registros", "error");
    } finally {
      setLoading(false);
    }
  }, [tipo, search, estatusFiltro, categoriaFiltro, page, token]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Categorías activas para el formulario y el filtro.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/categorias`, { credentials: "include", headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setCategorias(await res.json());
      } catch (error) {
        console.error("Error cargando categorías:", error);
      }
    })();
  }, [token]);

  useEffect(() => {
    setPage(1);
  }, [tipo]);

  const abrirNuevo = () => {
    setFormData({ tipo, fecha: "", concepto: "", cantidad: "", id_categoria: "" });
    setEditando(false);
    setShowModal(true);
  };

  const abrirEditar = (row) => {
    setFormData({ ...row, fecha: row.fecha ? row.fecha.split("T")[0] : "", id_categoria: row.id_categoria ?? "" });
    setEditando(true);
    setShowModal(true);
  };

  const guardar = async () => {
    if (!formData.fecha || !formData.concepto?.trim() || !formData.cantidad) {
      mostrarToast("Fecha, concepto y cantidad son obligatorios", "error");
      return;
    }

    const url = editando ? `${API}/${formData.id}` : API;
    const method = editando ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        mostrarToast("Error guardando el registro", "error");
        return;
      }

      mostrarToast(editando ? "Registro actualizado" : "Registro creado");
      setShowModal(false);
      cargarDatos();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  const cambiarEstado = async (row) => {
    const activar = !row.activo;
    try {
      const res = await fetch(`${API}/${row.id}${activar ? "/reactivar" : ""}`, {
        method: activar ? "PATCH" : "DELETE",
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        mostrarToast("Error actualizando el estatus", "error");
        return;
      }

      mostrarToast(activar ? "Registro reactivado" : "Registro desactivado");
      cargarDatos();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  const inicioRango = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const finRango = Math.min(page * LIMIT, total);

  return (
    <div className="p-6 bg-slate-50">

      {/* BREADCRUMB */}
      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/finanzas" className="hover:text-indigo-600">Finanzas</Link>
        <span className="mx-1.5">/</span>
        <span className="text-slate-600 font-medium">{tiposNombres[tipo]}</span>
      </div>

      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{tiposNombres[tipo]}</h1>
          <p className="text-sm text-slate-500 mt-0.5">Registra y administra las salidas de dinero.</p>
        </div>

        <button
          onClick={abrirNuevo}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
        >
          <FaPlus className="text-xs" /> Nueva salida
        </button>
      </div>

      {encabezadoExtra}

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
          <input
            placeholder="Buscar por concepto..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Categoría</label>
          <select
            value={categoriaFiltro}
            onChange={(e) => {
              setCategoriaFiltro(e.target.value);
              setPage(1);
            }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="">Todas</option>
            <option value="0">Sin categoría</option>
            {categorias.map((c) => (
              <option key={c.id_categoria} value={c.id_categoria}>{c.nombre}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Estatus</label>
          <select
            value={estatusFiltro}
            onChange={(e) => {
              setEstatusFiltro(e.target.value);
              setPage(1);
            }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Concepto</th>
                <th className="px-4 py-3 text-left font-semibold">Categoría</th>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-left font-semibold">Cantidad</th>
                <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                <th className="px-4 py-3 text-left font-semibold">Estatus</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-slate-400">
                    Cargando...
                  </td>
                </tr>
              ) : data.length > 0 ? (
                data.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t border-slate-100 hover:bg-slate-50 transition"
                  >
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2 font-medium text-slate-800">
                        <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                          <FaMoneyBillWave className="text-xs" />
                        </span>
                        {row.concepto}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.categoria || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{formatoFecha(row.fecha)}</td>
                    <td className="px-4 py-3 text-slate-600 font-medium">{formatoMoneda(row.cantidad)}</td>
                    <td className="px-4 py-3 text-slate-600">{row.usuario_nombre || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2 text-slate-600">
                        <span className={`w-2 h-2 rounded-full ${row.activo ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {row.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => abrirEditar(row)}
                          title="Editar"
                          className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                        >
                          <FaPencilAlt className="text-xs" />
                        </button>

                        {row.activo ? (
                          <button
                            onClick={() => cambiarEstado(row)}
                            title="Desactivar"
                            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition"
                          >
                            <FaBan className="text-xs" />
                          </button>
                        ) : (
                          <button
                            onClick={() => cambiarEstado(row)}
                            title="Reactivar"
                            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition"
                          >
                            <FaCheck className="text-xs" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-slate-400">
                    Sin resultados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINACIÓN */}
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-slate-500">
          {total === 0 ? "Sin resultados" : `Mostrando ${inicioRango} a ${finRango} de ${total} resultados`}
        </span>

        <div className="flex items-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
          >
            <FaChevronLeft className="text-xs" />
          </button>

          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-indigo-600 text-white text-sm font-semibold">
            {page}
          </span>

          <button
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(page + 1)}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>

      {/* MODAL NUEVO / EDITAR */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editando ? "Editar salida" : "Nueva salida"}
      >
        <div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Fecha <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                value={formData.fecha || ""}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Concepto <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={formData.concepto || ""}
                onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Categoría</label>
              <select
                value={formData.id_categoria ?? ""}
                onChange={(e) => setFormData({ ...formData, id_categoria: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 bg-white"
              >
                <option value="">Sin categoría</option>
                {categorias.map((c) => (
                  <option key={c.id_categoria} value={c.id_categoria}>{c.nombre}</option>
                ))}
                {/* Categoría ya desactivada que la salida conserva */}
                {formData.id_categoria && !categorias.some((c) => String(c.id_categoria) === String(formData.id_categoria)) && (
                  <option value={formData.id_categoria}>{formData.categoria || "Categoría desactivada"}</option>
                )}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Cantidad <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.cantidad || ""}
                onChange={(e) => setFormData({ ...formData, cantidad: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
            <button
              onClick={() => setShowModal(false)}
              className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={guardar}
              className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition"
            >
              Guardar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
